export const RATING = {
  baseElo: 1500,
  k: 32,
  eloPerOvr: 12,
  startOvr: 75,
  minOvr: 40,
  maxOvr: 99,
};

export const toOvr = (elo) =>
  Math.max(
    RATING.minOvr,
    Math.min(RATING.maxOvr, Math.round(RATING.startOvr + (elo - RATING.baseElo) / RATING.eloPerOvr)),
  );

export const expectedScore = (eloA, eloB) => 1 / (1 + 10 ** ((eloB - eloA) / 400));

export function rateGame(homeElo, awayElo, homeScore, awayScore) {
  const result = homeScore > awayScore ? 1 : 0;
  const margin = Math.log(Math.abs(homeScore - awayScore) + 1);
  const change = RATING.k * margin * (result - expectedScore(homeElo, awayElo));
  return [homeElo + change, awayElo - change];
}

const emptyRecord = () => ({ wins: 0, losses: 0, form: [] });

export function computeLeague(players, games) {
  const elo = {};
  const record = {};
  const perGame = {};
  players.forEach((player) => {
    elo[player.id] = RATING.baseElo;
    record[player.id] = emptyRecord();
  });
  [...games]
    .sort((a, b) => a.createdAt - b.createdAt)
    .forEach((game) => {
      if (elo[game.home] == null || elo[game.away] == null) return;
      const before = { home: elo[game.home], away: elo[game.away] };
      const [home, away] = rateGame(before.home, before.away, game.homeScore, game.awayScore);
      perGame[game.id] = {
        homeDelta: toOvr(home) - toOvr(before.home),
        awayDelta: toOvr(away) - toOvr(before.away),
        homeWinChance: expectedScore(before.home, before.away),
      };
      elo[game.home] = home;
      elo[game.away] = away;
      const homeWon = game.homeScore > game.awayScore;
      record[game.home][homeWon ? 'wins' : 'losses'] += 1;
      record[game.away][homeWon ? 'losses' : 'wins'] += 1;
      record[game.home].form.push(homeWon ? 'w' : 'l');
      record[game.away].form.push(homeWon ? 'l' : 'w');
    });
  return { elo, record, perGame };
}

export const ovrOf = (league, id) => toOvr(league.elo[id] ?? RATING.baseElo);

export function previewDeltas(league, home, away, homeScore, awayScore) {
  const h = league.elo[home] ?? RATING.baseElo;
  const a = league.elo[away] ?? RATING.baseElo;
  const [nh, na] = rateGame(h, a, homeScore, awayScore);
  return { home: toOvr(nh) - toOvr(h), away: toOvr(na) - toOvr(a) };
}

export function standardInnings(games, fallback = 9) {
  const counts = new Map();
  games.forEach((game) => counts.set(game.innings, (counts.get(game.innings) ?? 0) + 1));
  let best = fallback;
  let bestCount = 0;
  counts.forEach((count, innings) => {
    if (count > bestCount) {
      best = innings;
      bestCount = count;
    }
  });
  return best;
}

export function rankPlayers(players, league) {
  return players
    .map((player) => ({
      player,
      ovr: ovrOf(league, player.id),
      record: league.record[player.id] ?? emptyRecord(),
    }))
    .sort(
      (a, b) =>
        b.ovr - a.ovr ||
        b.record.wins - a.record.wins ||
        a.player.name.localeCompare(b.player.name),
    );
}
